import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { getGoogleMapsMapId, getMapColorScheme, loadGoogleMaps } from '../../utils/googleMapsLoader';
import Navigation from '../../components/feature/Navigation';
import MapSidebar from '../../components/map/MapSidebar';
import type { Property } from '../../mocks/properties';
import { mockProperties } from '../../mocks/properties';
import { usePropertyStore } from '../../hooks/usePropertyStore';
import { searchProperties as ragSearch } from '../../services/ragSearchService';
import { formatAmount } from '../../utils/numberFormat';

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';

const formatRiyalNumber = (value: number) => formatAmount(value, { maximumFractionDigits: 0 });

const DEFAULT_CENTER = { lat: 24.7136, lng: 46.6753 }; // Riyadh
const DEFAULT_ZOOM = 6; // Show all of Saudi Arabia

// AdvancedMarkerElement content builders (DOM elements replace the old icon/label).
function createPriceChip(text: string): HTMLElement {
  const el = document.createElement('div');
  el.style.cssText =
    'background:#fff;border:1.5px solid #004D40;border-radius:9999px;padding:3px 8px;' +
    'font-size:11px;font-weight:700;color:#004D40;white-space:nowrap;font-family:sans-serif;' +
    'box-shadow:0 1px 4px rgba(0,0,0,.2);';
  el.textContent = text;
  return el;
}

function createClusterBubble(count: number): HTMLElement {
  const el = document.createElement('div');
  el.style.cssText =
    'display:flex;align-items:center;justify-content:center;width:40px;height:40px;' +
    'border-radius:9999px;background:#004D40;border:2px solid #fff;color:#fff;' +
    'font-size:12px;font-weight:700;font-family:sans-serif;box-shadow:0 1px 6px rgba(0,0,0,.3);';
  el.textContent = String(count);
  return el;
}

// Delegate to the single shared loader. This page previously injected its own
// script tag (and without `loading=async`), so it could load the Maps API a
// second time into a separate realm — the cause of "not an instance of
// MapsApiMap" on AdvancedMarkerElement. One loader app-wide = one realm.
function loadGoogleMapsScript(): Promise<void> {
  return loadGoogleMaps();
}

export default function MapPage() {
  const { t, i18n } = useTranslation(['search', 'property', 'common']);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getAvailableProperties } = usePropertyStore();

  const [properties, setProperties] = useState<Property[]>([]);
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  const [isMapReady, setIsMapReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [drawingMode, setDrawingMode] = useState(false);
  const [drawnPolygon, setDrawnPolygon] = useState<google.maps.Polygon | null>(null);

  const mapRef = useRef<HTMLDivElement | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mapInstanceRef = useRef<any>(null);
  const markersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);
  const clustererRef = useRef<MarkerClusterer | null>(null);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const drawingManagerRef = useRef<google.maps.drawing.DrawingManager | null>(null);

  const direction = i18n.dir();

  const propertyTranslations = useMemo(() => {
    const data = t('property:properties', { returnObjects: true, defaultValue: {} }) as Record<
      string,
      Record<string, unknown>
    >;
    return data ?? {};
  }, [t, i18n.language]);

  const getLocalizedField = useCallback(
    (property: Property, key: string, fallback: string): string => {
      const override = propertyTranslations[property.id];
      if (override && typeof override[key] === 'string' && (override[key] as string).length > 0) {
        return override[key] as string;
      }
      return fallback;
    },
    [propertyTranslations],
  );

  // Load properties
  useEffect(() => {
    async function loadProperties() {
      setIsLoading(true);
      try {
        const query = searchParams.get('q') || '';
        const result = await ragSearch({ q: query, limit: 100, lang: i18n.language === 'ar' ? 'ar' : 'en' });

        if (result.success && result.data?.properties) {
          // Convert API properties to frontend format
          const apiProperties = result.data.properties.map((p) => ({
            id: p.id,
            title: p.title,
            price: p.price,
            bedrooms: p.bedrooms,
            bathrooms: p.bathrooms,
            sqft: p.sqft || 1000,
            address: p.address || '',
            city: p.city,
            state: '',
            zipCode: '',
            latitude: p.latitude || 24.7136,
            longitude: p.longitude || 46.6753,
            imageUrl: p.imageUrl || '',
            description: '',
            propertyType: (p.propertyType as Property['propertyType']) || 'Apartment',
            yearBuilt: 2020,
            parking: false,
            petFriendly: p.isPetFriendly || false,
            furnished: p.isFurnished || false,
            utilities: [],
            amenities: [],
            agentName: '',
            agentPhone: '',
            agentEmail: '',
            dateAdded: new Date().toISOString(),
            status: 'available' as const,
            featured: p.isFeatured || false,
            pricePerSqft: 0,
            lastUpdated: new Date().toISOString(),
          }));
          setProperties(apiProperties);
        } else {
          // Fallback to local properties
          const localProps = [...getAvailableProperties(), ...mockProperties];
          setProperties(localProps);
        }
      } catch {
        const localProps = [...getAvailableProperties(), ...mockProperties];
        setProperties(localProps);
      }
      setIsLoading(false);
    }

    loadProperties();
  }, [searchParams, i18n.language, getAvailableProperties]);

  // Clean up markers
  const cleanupMarkers = useCallback(() => {
    if (clustererRef.current) {
      clustererRef.current.clearMarkers();
    }
    markersRef.current.forEach((marker) => {
      marker.map = null;
    });
    markersRef.current = [];
  }, []);

  // Handle property selection
  const handlePropertySelect = useCallback((property: Property) => {
    setSelectedProperty(property);
    const mapInstance = mapInstanceRef.current;
    if (mapInstance) {
      mapInstance.panTo({ lat: property.latitude, lng: property.longitude });
      mapInstance.setZoom(15);
    }
  }, []);

  // View property details
  const handleViewProperty = useCallback((property: Property) => {
    navigate(`/property/${property.id}`);
  }, [navigate]);

  // Create marker popup
  const createMarkerPopup = useCallback(
    (property: Property) => {
      const title = getLocalizedField(property, 'title', property.title);
      const address = getLocalizedField(property, 'address', property.address);
      const city = getLocalizedField(property, 'city', property.city);
      const priceFormatted = formatRiyalNumber(property.price);
      const textAlign = direction === 'rtl' ? 'right' : 'left';
      const agentName = property.agentName || (direction === 'rtl' ? 'وكيل العقار' : 'Property Agent');
      const agentPhone = property.agentPhone || '';
      const waPhone = agentPhone.replace(/\D/g, '');
      const agentCompany = (property as unknown as Record<string, unknown>).agentCompany as string | undefined;
      const agentType = ((property as unknown as Record<string, unknown>).agentType as string) || 'broker';
      const agentSubtitle = agentCompany ? agentCompany : (agentType === 'owner' ? (direction === 'rtl' ? 'مالك العقار' : 'Property Owner') : (direction === 'rtl' ? 'وكيل معتمد' : 'Verified Agent'));

      return `
        <div style="min-width:260px;max-width:300px;font-family:'Tajawal',Inter,system-ui,sans-serif;direction:${direction};text-align:${textAlign};padding:10px;">
          <strong style="display:block;font-size:14px;margin-bottom:4px;color:#0F1A18;">${title}</strong>
          <span style="display:block;font-size:12px;color:#455855;margin-bottom:8px;">${address}, ${city}</span>
          <div style="display:flex;align-items:center;gap:6px;font-size:14px;font-weight:700;color:#004D40;margin-bottom:10px;background:#F2F8F7;padding:6px 10px;border-radius:8px;">
            <span>&#x0631;.&#x0633;</span>
            <span>${priceFormatted}</span>
          </div>
          <div style="border-top:1px solid #E0E9E7;padding-top:8px;margin-bottom:8px;">
            <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px;">
              <div style="width:32px;height:32px;border-radius:50%;background:linear-gradient(135deg,${agentCompany ? '#055A4C,#004D40' : '#004D40,#1B7566'});color:white;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:700;flex-shrink:0;">${agentCompany ? '🏢' : agentName.charAt(0)}</div>
              <div>
                <div style="font-size:13px;font-weight:600;color:#0F1A18;">${agentName}</div>
                <div style="font-size:10px;color:#004D40;">${agentSubtitle}</div>
                ${agentPhone ? `<div style="font-size:11px;color:#617672;">${agentPhone}</div>` : ''}
              </div>
            </div>
            <div style="display:flex;gap:6px;">
              ${agentPhone ? `<a href="tel:${agentPhone}" style="flex:1;display:flex;align-items:center;justify-content:center;gap:4px;padding:6px;background:#F2F8F7;color:#004D40;border-radius:8px;font-size:11px;font-weight:600;text-decoration:none;border:1px solid #DDEEEB;">📞 ${direction === 'rtl' ? 'اتصال' : 'Call'}</a>` : ''}
              ${waPhone ? `<a href="https://wa.me/${waPhone}" target="_blank" rel="noopener noreferrer" style="flex:1;display:flex;align-items:center;justify-content:center;gap:4px;padding:6px;background:#f0fdf4;color:#16a34a;border-radius:8px;font-size:11px;font-weight:600;text-decoration:none;border:1px solid #dcfce7;">💬 WhatsApp</a>` : ''}
            </div>
          </div>
          <button onclick="window.viewMapProperty && window.viewMapProperty('${property.id}')" style="display:block;width:100%;background:linear-gradient(135deg,#004D40,#1B7566);color:white;border-radius:10px;padding:10px 0;font-size:12px;font-weight:600;border:none;cursor:pointer;box-shadow:0 2px 8px rgba(0,77,64,0.3);">
            ${t('search.card.viewDetails')}
          </button>
        </div>
      `;
    },
    [direction, getLocalizedField, t],
  );

  // Filter properties by polygon
  const filterPropertiesByPolygon = useCallback((polygon: google.maps.Polygon, allProperties: Property[]) => {
    return allProperties.filter((property) => {
      const point = new google.maps.LatLng(property.latitude, property.longitude);
      return google.maps.geometry.poly.containsLocation(point, polygon);
    });
  }, []);

  // Toggle drawing mode
  const toggleDrawingMode = useCallback(() => {
    const drawingManager = drawingManagerRef.current;
    if (!drawingManager) return;

    if (drawingMode) {
      drawingManager.setDrawingMode(null);
      if (drawnPolygon) {
        drawnPolygon.setMap(null);
        setDrawnPolygon(null);
      }
    } else {
      drawingManager.setDrawingMode(google.maps.drawing.OverlayType.POLYGON);
    }
    setDrawingMode(!drawingMode);
  }, [drawingMode, drawnPolygon]);

  // Clear drawing
  const clearDrawing = useCallback(() => {
    if (drawnPolygon) {
      drawnPolygon.setMap(null);
      setDrawnPolygon(null);
    }
    setDrawingMode(false);
    if (drawingManagerRef.current) {
      drawingManagerRef.current.setDrawingMode(null);
    }
  }, [drawnPolygon]);

  // Initialize map
  useEffect(() => {
    if (!GOOGLE_MAPS_API_KEY) {
      setError('Google Maps API key not configured');
      setIsMapReady(false);
      return;
    }

    let isMounted = true;

    async function initMap() {
      try {
        await loadGoogleMapsScript();

        if (!isMounted || !mapRef.current) return;

        // Create the map
        mapInstanceRef.current = new google.maps.Map(mapRef.current, {
          center: DEFAULT_CENTER,
          zoom: DEFAULT_ZOOM,
          // Required for AdvancedMarkerElement. With a Map ID, map styling (e.g.
          // hiding POI labels) is configured via cloud-based styles, so the inline
          // `styles` option is no longer used here.
          mapId: getGoogleMapsMapId(),
          colorScheme: getMapColorScheme(),
          mapTypeControl: true,
          streetViewControl: false,
          fullscreenControl: true,
          zoomControl: false,
        });

        // Create shared info window
        infoWindowRef.current = new google.maps.InfoWindow();

        // Initialize Drawing Manager
        drawingManagerRef.current = new google.maps.drawing.DrawingManager({
          drawingMode: null,
          drawingControl: false,
          polygonOptions: {
            fillColor: '#004D40',
            fillOpacity: 0.2,
            strokeColor: '#004D40',
            strokeWeight: 2,
            editable: true,
            draggable: true,
          },
        });
        drawingManagerRef.current.setMap(mapInstanceRef.current);

        // Handle polygon complete.
        // The addListener signature in @types/google.maps is generic
        // `(...args: unknown[]) => void`; we narrow the first arg back to
        // Polygon inside the handler.
        google.maps.event.addListener(
          drawingManagerRef.current,
          'polygoncomplete',
          (...args: unknown[]) => {
            const polygon = args[0] as google.maps.Polygon;
            if (drawnPolygon) {
              drawnPolygon.setMap(null);
            }
            setDrawnPolygon(polygon);
            setDrawingMode(false);
            drawingManagerRef.current?.setDrawingMode(null);
          }
        );

        setIsMapReady(true);
        setError(null);
      } catch (err) {
        console.error('Error initializing map:', err);
        if (isMounted) {
          setError('Failed to load map');
          setIsMapReady(false);
        }
      }
    }

    initMap();

    return () => {
      isMounted = false;
      cleanupMarkers();
    };
  }, [cleanupMarkers]);

  // Create markers with clustering
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    const infoWindow = infoWindowRef.current;

    if (!mapInstance || !isMapReady || !window.google?.maps) {
      return;
    }

    cleanupMarkers();

    // Filter properties if polygon is drawn
    let displayProperties = properties;
    if (drawnPolygon) {
      displayProperties = filterPropertiesByPolygon(drawnPolygon, properties);
    }

    // Set up global handler for popup button clicks
    window.viewMapProperty = (propertyId: string) => {
      navigate(`/property/${propertyId}`);
    };

    const markers = displayProperties.map((property) => {
      const marker = new google.maps.marker.AdvancedMarkerElement({
        position: { lat: property.latitude, lng: property.longitude },
        title: getLocalizedField(property, 'title', property.title),
        content: createPriceChip(formatRiyalNumber(property.price)),
      });

      marker.addListener('gmp-click', () => {
        if (infoWindow) {
          infoWindow.setContent(createMarkerPopup(property));
          infoWindow.open(mapInstance, marker as unknown as google.maps.Marker);
        }
        setSelectedProperty(property);
      });

      return marker;
    });

    markersRef.current = markers;

    // Add clustering
    clustererRef.current = new MarkerClusterer({
      map: mapInstance,
      markers,
      onClusterClick: (_event, cluster, map) => {
        const bounds = cluster.bounds;
        if (bounds) {
          map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
          // Prevent zooming in too much - cap at zoom 16
          const listener = google.maps.event.addListenerOnce(map, 'idle', () => {
            const currentZoom = map.getZoom();
            if (currentZoom !== undefined && currentZoom > 16) {
              map.setZoom(16);
            }
            google.maps.event.removeListener(listener);
          });
        }
      },
      renderer: {
        render: ({ count, position }) => {
          return new google.maps.marker.AdvancedMarkerElement({
            position: { lat: position.lat(), lng: position.lng() },
            content: createClusterBubble(count),
            zIndex: 1000 + count,
          });
        },
      },
    });

    return () => {
      window.viewMapProperty = undefined;
    };
  }, [properties, isMapReady, drawnPolygon, cleanupMarkers, createMarkerPopup, getLocalizedField, navigate, filterPropertiesByPolygon]);

  // Handle zoom controls
  const handleZoomIn = () => {
    const mapInstance = mapInstanceRef.current;
    if (mapInstance) {
      mapInstance.setZoom((mapInstance.getZoom() || DEFAULT_ZOOM) + 1);
    }
  };

  const handleZoomOut = () => {
    const mapInstance = mapInstanceRef.current;
    if (mapInstance) {
      mapInstance.setZoom((mapInstance.getZoom() || DEFAULT_ZOOM) - 1);
    }
  };

  const handleResetView = () => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance) return;

    if (properties.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      properties.forEach((property) => {
        bounds.extend({ lat: property.latitude, lng: property.longitude });
      });
      mapInstance.fitBounds(bounds);
    } else {
      mapInstance.setCenter(DEFAULT_CENTER);
      mapInstance.setZoom(DEFAULT_ZOOM);
    }
    clearDrawing();
  };

  // Filter displayed properties
  const displayedProperties = useMemo(() => {
    if (drawnPolygon && window.google?.maps?.geometry) {
      return filterPropertiesByPolygon(drawnPolygon, properties);
    }
    return properties;
  }, [properties, drawnPolygon, filterPropertiesByPolygon]);

  if (error) {
    return (
      <div className="flex flex-col min-h-screen bg-[var(--pv-color-surface)]">
        <Navigation />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center text-slate-500">
            <i className="ri-map-pin-line text-4xl mb-2"></i>
            <p className="text-sm">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-[var(--pv-color-surface)]" dir={direction}>
      <Navigation />

      <div className="flex-1 flex relative overflow-hidden">
        {/* Sidebar */}
        <MapSidebar
          properties={displayedProperties}
          selectedProperty={selectedProperty}
          onPropertySelect={handlePropertySelect}
          onPropertyView={handleViewProperty}
          isOpen={sidebarOpen}
          onToggle={() => setSidebarOpen(!sidebarOpen)}
          isLoading={isLoading}
        />

        {/* Map Container */}
        <div className="flex-1 relative">
          <div ref={mapRef} className="absolute inset-0" />

          {/* Loading overlay */}
          {(!isMapReady || isLoading) && (
            <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/85 dark:bg-[var(--pv-color-surface)]/85">
              <div className="text-center">
                <i className="ri-loader-4-line mb-4 text-4xl text-[var(--pv-color-primary)] animate-spin"></i>
                <p className="text-gray-600 dark:text-gray-300">{t('search.map.loading')}</p>
              </div>
            </div>
          )}

          {/* Property count badge */}
          <div className="absolute top-4 left-4 z-30 rounded-lg border border-gray-200 dark:border-[var(--pv-border-soft)] bg-white dark:bg-[var(--pv-color-surface-strong)] px-4 py-2 shadow-md">
            <span className="text-sm font-medium text-gray-900 dark:text-[var(--pv-color-text)]">
              {t('search.map.countLabel', { count: displayedProperties.length })}
            </span>
          </div>

          {/* Drawing controls */}
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-30 flex gap-2">
            <button
              type="button"
              onClick={toggleDrawingMode}
              className={[
                'flex items-center gap-2 px-4 py-2 rounded-lg border shadow-md transition-colors',
                drawingMode
                  ? 'bg-[var(--pv-color-primary)] text-white border-[var(--pv-color-primary)]'
                  : 'bg-white dark:bg-[var(--pv-color-surface-strong)] border-gray-200 dark:border-[var(--pv-border-soft)] text-gray-700 dark:text-[var(--pv-color-text)] hover:bg-gray-50 dark:hover:bg-[var(--pv-color-surface-soft)]',
              ].join(' ')}
            >
              <i className="ri-shape-line"></i>
              <span className="text-sm font-medium">{t('search.map.drawSearch', { defaultValue: 'Draw to search' })}</span>
            </button>
            {drawnPolygon && (
              <button
                type="button"
                onClick={clearDrawing}
                className="flex items-center gap-2 px-4 py-2 rounded-lg border border-red-200 bg-red-50 text-red-600 shadow-md hover:bg-red-100 transition-colors"
              >
                <i className="ri-close-line"></i>
                <span className="text-sm font-medium">{t('search.map.clearDrawing', { defaultValue: 'Clear' })}</span>
              </button>
            )}
          </div>

          {/* Zoom controls */}
          <div className="absolute top-4 right-4 z-30 overflow-hidden rounded-lg border border-gray-200 dark:border-[var(--pv-border-soft)] bg-white dark:bg-[var(--pv-color-surface-strong)] shadow-md">
            <button
              type="button"
              className="border-b border-gray-200 dark:border-[var(--pv-border-soft)] p-3 transition-colors hover:bg-gray-50 dark:hover:bg-[var(--pv-color-surface-soft)]"
              title={t('search.map.zoomIn')}
              onClick={handleZoomIn}
            >
              <i className="ri-zoom-in-line text-gray-600 dark:text-[var(--pv-color-text-muted)]"></i>
            </button>
            <button
              type="button"
              className="border-b border-gray-200 dark:border-[var(--pv-border-soft)] p-3 transition-colors hover:bg-gray-50 dark:hover:bg-[var(--pv-color-surface-soft)]"
              title={t('search.map.zoomOut')}
              onClick={handleZoomOut}
            >
              <i className="ri-zoom-out-line text-gray-600 dark:text-[var(--pv-color-text-muted)]"></i>
            </button>
            <button
              type="button"
              className="p-3 transition-colors hover:bg-gray-50 dark:hover:bg-[var(--pv-color-surface-soft)]"
              title={t('search.map.reset')}
              onClick={handleResetView}
            >
              <i className="ri-focus-3-line text-gray-600 dark:text-[var(--pv-color-text-muted)]"></i>
            </button>
          </div>

          {/* Empty state */}
          {displayedProperties.length === 0 && isMapReady && !isLoading && (
            <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/85 dark:bg-[var(--pv-color-surface)]/85">
              <div className="text-center">
                <i className="ri-map-2-line mb-4 text-4xl text-gray-400"></i>
                <p className="text-gray-600 dark:text-gray-300">{t('search.map.empty')}</p>
                {drawnPolygon && (
                  <button
                    type="button"
                    onClick={clearDrawing}
                    className="mt-4 px-4 py-2 rounded-lg bg-[var(--pv-color-primary)] text-white text-sm font-medium"
                  >
                    {t('search.map.clearDrawing', { defaultValue: 'Clear drawing' })}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Extend window for global property view handler
declare global {
  interface Window {
    viewMapProperty?: (propertyId: string) => void;
  }
}
